import { Instant, IsoFields } from "@js-joda/core";
import { DatasetDataInfoResponse } from "api/model/dataset/DatasetDataInfoResponse";
import { useTimezone } from "hooks/useTimezone";
import { Tooltip } from "primereact/tooltip";
import React from "react";
import { T } from "services/lang/LanguageService";
import { safeIdString } from "utils/utils";
import getCurrentDate from "../current-date-display/CurrentDate";
import {
  DataDisplayType,
  DiffType,
  DiffValueOptions,
  ValueChangeIndicatorProps,
} from "./ValueChangeIndicatorTypes";
import {
  getArrowDirection,
  getDiffClass,
  getDiffClassName,
  getWeekYearComparison,
} from "./ValueChangeIndicatorUtils";

const DiffValueForWeekYearComparison = (
  props: ValueChangeIndicatorProps & {
    currentDiffValueOptions: DiffValueOptions;
    refTime: Instant;
    dataRange?: DatasetDataInfoResponse[];
  }
) => {
  const tz = useTimezone();
  if (props.dataDisplayType !== DataDisplayType.WEEK_YEAR_COMPARISON || !props.dataRange) return null;
  const dataset = props.dataRange[0];
  const safeDiffId = safeIdString(dataset.identifier);
  const diffTooltipId = `week-year-diff-${safeDiffId}-tooltip`;

  if (props.useHorizontalBarChart) {
    if (!props.currentDiffValueOptions.isHorizontalBarChartDiffLoaded) return null;

    const diffValue = props.currentDiffValueOptions.value ?? 0;
    const isNegative = diffValue < 0;
    const isNeutral = Number(diffValue.toFixed(props.diffValueDecimalPlaces)) === 0;
    const diffClass = getDiffClass(isNegative);
    return (
      <>
        <div
          id={diffTooltipId}
          className={getDiffClassName(isNeutral, props.dataDisplayType, props.useDiffIndicatorColors)}
        >
          <div className={diffClass}>
            {props.diffType === DiffType.PERCENTAGE
              ? `${diffValue.toFixed(props.diffValueDecimalPlaces)}%`
              : `${diffValue.toFixed(props.diffValueDecimalPlaces)} ${props.dataRange[0].unit}`}
          </div>
          {isNeutral && <i className="pi pi-arrows-v" />}
          {!isNeutral && <i className={`pi pi-arrow-${getArrowDirection(isNegative)} ${diffClass}`} />}
        </div>

        <Tooltip target={`#${diffTooltipId}`} mouseTrack mouseTrackLeft={10} position="bottom">
          <div className="flex flex-column gap-1">
            <div>{props.currentDiffValueOptions.previousValueInfo}</div>
            <div>{props.currentDiffValueOptions.currentValueInfo}</div>
          </div>
        </Tooltip>
      </>
    );
  }
  const comparison = getWeekYearComparison(dataset, tz.company, props.refTime);

  if (!comparison || comparison.current == null || comparison.comparisons.length === 0) {
    return null;
  }
  const prev = comparison.comparisons[0].value;
  const curr = comparison.current;

  const findPoint = (year: number) =>
    dataset.data.find(point => {
      const zdt = Instant.parse(point.key).atZone(tz.company);
      return zdt.get(IsoFields.WEEK_OF_WEEK_BASED_YEAR) === comparison.refWeek && zdt.year() === year;
    });

  const prevPoint = findPoint(comparison.comparisons[0].year);
  const currPoint = findPoint(comparison.refYear);

  const prevYear = prevPoint && Instant.parse(prevPoint.key).atZone(tz.company).year();
  const currYear = currPoint && Instant.parse(currPoint.key).atZone(tz.company).year();

  const diffValue = curr - prev;
  const roundedDiff = Number(diffValue.toFixed(props.diffValueDecimalPlaces));

  const isNegative = diffValue < 0;
  const isNeutral = roundedDiff === 0;

  const week = getCurrentDate({
    timezone: tz.display,
    displayWeekOfYear: true,
    year: undefined,
    month: undefined,
    day: undefined,
    refTime: props.refTime,
  });

  let diffLabel: string;

  if (props.diffType === DiffType.PERCENTAGE) {
    let percentageChange: number;

    if (prev === 0) {
      if (curr === 0) {
        percentageChange = 0;
      } else {
        percentageChange = 100;
      }
    } else {
      percentageChange = ((curr - prev) / prev) * 100;
    }

    diffLabel = `${percentageChange.toFixed(props.diffValueDecimalPlaces)}%`;
  } else {
    diffLabel = `${diffValue.toFixed(props.diffValueDecimalPlaces)} ${dataset.unit}`;
  }

  const diffClass = getDiffClass(isNegative);
  return (
    <>
      <div
        id={diffTooltipId}
        className={getDiffClassName(isNeutral, props.dataDisplayType, props.useDiffIndicatorColors)}
      >
        <div className={diffClass}>{diffLabel}</div>
        {isNeutral && <i className="pi pi-arrows-v" />}
        {!isNeutral && <i className={`pi pi-arrow-${getArrowDirection(isNegative)} ${diffClass}`} />}
      </div>

      <Tooltip target={`#${diffTooltipId}`} mouseTrack mouseTrackLeft={10} position="bottom">
        <div className="flex flex-column gap-1">
          <div>
            <b>{T("word.previous-value")}:</b> {prev.toFixed(props.diffValueDecimalPlaces)} {dataset.unit}
            {prevPoint && (
              <span className="ml-2">
                {prevYear} {week}
              </span>
            )}
          </div>
          <div>
            <b>{T("word.current-value")}:</b> {curr.toFixed(props.diffValueDecimalPlaces)} {dataset.unit}
            {currPoint && (
              <span className="ml-2">
                {currYear} {week}
              </span>
            )}
          </div>
        </div>
      </Tooltip>
    </>
  );
};
export default DiffValueForWeekYearComparison;
